import React from 'react';
import PropTypes from 'prop-types';
import castArray from 'lodash/castArray';
import { VariableSizeList as List } from 'react-window';
import './_StickyHeaderListMentionAdapter.scss';

const HEADER_CLASS_NAME = 'StickyHeaderListMentionAdapter__header';
const HEADER_HEIGHT = 28;
const SUGGESTION_HEIGHT = 29;

class StickyHeaderListMentionAdapter extends React.PureComponent {
  // The MentionSuggestions component is directly accessing the style property of the component, and not via props.
  // This means that we need to trigger a render each time this changes since this is the style of the entire container.
  get style() {
    return this.state.containerStyle;
  }

  set style(value) {
    this.setState({ containerStyle: value });
  }

  constructor(props) {
    super(props);

    this.state = {
      containerStyle: {},
      currentHeaderIndex: 0
    };
    this.outerRef = React.createRef();
    this.listRef = React.createRef();

    this.focusedSuggestionIndex = 0;
    this.isScrolling = false;
    this.elementList = [];
    this.renderedRows = {};
    this.suggestionsStyle = [];
  }

  isHeader = element => element && element.props.className === HEADER_CLASS_NAME;

  isElementInFocus = element => element && element.props.isFocused;

  buildGroups(props = this.props) {
    return Object.values(
      castArray(props.children).reduce((groups, child) => {
        if (!child.props) return groups;

        const groupHeader = child.props.mention.get('type');

        groups[groupHeader] = groups[groupHeader] || {
          header: groupHeader,
          id: `__StickyHeaderListMentionAdapter__group-id__${groupHeader}`,
          items: []
        };

        groups[groupHeader].items = groups[groupHeader].items.concat([child]);
        return groups;
      }, {})
    ).reduce(
      ({ suggestions, headerOriginalIndices }, group) => {
        return {
          headerOriginalIndices: [...headerOriginalIndices, suggestions.length],
          suggestions: [
            ...suggestions,
            <div
              className={HEADER_CLASS_NAME}
              style={{ height: HEADER_HEIGHT }}
              key={`group-${group.id}`}
              id={group.id}
            >
              {group.header}
            </div>,
            ...group.items
          ]
        };
      },
      { suggestions: [], headerOriginalIndices: []}
    );
  }

  componentDidUpdate() {
    this.handleFocusChange();
    const focusedSuggestionIsRenderedInView =
      this.renderedRows.visibleStartIndex < this.focusedSuggestionIndex &&
      this.focusedSuggestionIndex < this.renderedRows.visibleStopIndex;
    if (!focusedSuggestionIsRenderedInView && this.focusedSuggestionIndex && !this.isScrolling) {
      this.listRef.current.scrollToItem(
        this.focusedSuggestionIndex + (this.scrollDirectionIsDown ? 0 : -1)
      );
    }
  }

  rowRenderer = ({ index, style, isScrolling }) => {
    this.isScrolling = isScrolling;
    this.suggestionsStyle[index] = style.top >= 0 ? style : this.suggestionsStyle[index];
    if (this.isElementInFocus(this.elementList.suggestions[index])) {
      this.renderedElementInFocusIndex = index;
    }
    return (
      <div key={index} style={style}>
        {this.elementList.suggestions[index]}
      </div>
    );
  };

  getCurrentHeaderIndex = () => {
    return (
      this.state.currentHeaderIndex >= 0 &&
      this.elementList.headerOriginalIndices[this.state.currentHeaderIndex]
    );
  };

  getNextHeaderIndex = () => {
    return (
      this.state.currentHeaderIndex >= 0 &&
      this.elementList.headerOriginalIndices[this.state.currentHeaderIndex + 1]
    );
  };

  findCurrentHeaderIndex = renderedRows => {
    let currentHeaderIndex = 0;
    for (let i = 0; i < this.elementList.headerOriginalIndices.length; i++) {
      if (this.elementList.headerOriginalIndices[i] <= renderedRows.visibleStartIndex) {
        currentHeaderIndex = i;
      } else {
        break;
      }
    }
    return { currentHeaderIndex };
  };

  calcSuggestionHeight = entry => (this.isHeader(entry) ? HEADER_HEIGHT : SUGGESTION_HEIGHT);

  calcTotalHeight = (headerListLength, suggestionListLength) =>
    headerListLength * HEADER_HEIGHT + suggestionListLength * SUGGESTION_HEIGHT;

  onRowsRendered = renderedRows => {
    this.setState(this.findCurrentHeaderIndex(renderedRows));
    this.renderedRows = renderedRows;
  };

  getRowHeight = index => this.calcSuggestionHeight(this.elementList.suggestions[index]);

  innerElementTypeComponent = ({ style, children, ...rest }, ref) => {
    const scrollTop = this.outerRef.current ? this.outerRef.current.scrollTop : 0;
    const headerIndex = this.getCurrentHeaderIndex();
    const nextHeaderIndex = this.getNextHeaderIndex();
    const nextHeaderTop =
      this.suggestionsStyle[nextHeaderIndex] && this.suggestionsStyle[nextHeaderIndex].top;
    const offsetFromNextHeader =
      scrollTop -
      (nextHeaderTop || Number.MAX_VALUE) /*next header might not yet been rendered*/ +
      HEADER_HEIGHT;
    const headerTop = scrollTop - Math.max(0, offsetFromNextHeader);
    const defaultHeaderStyle = this.suggestionsStyle[0];
    return (
      <div ref={ref} style={style} {...rest}>
        {this.elementList.suggestions[headerIndex] &&
          React.cloneElement(this.elementList.suggestions[headerIndex], {
            style: {
              ...defaultHeaderStyle,
              zIndex: 1,
              top: 0,
              transform: `translateY(${headerTop}px)`,
              willChange: 'transform'
            }
          })}
        {children}
      </div>
    );
  };
  innerElementType = React.forwardRef(this.innerElementTypeComponent);

  findFocusedSuggestionIndex = () => {
    if (
      this.elementList.suggestions.length === 0 ||
      this.renderedRows.overscanStopIndex < this.renderedRows.overscanStartIndex
    )
      return -1;
    const [firstSuggestion, lastSuggestion] = [1, this.elementList.suggestions.length - 1];
    return [firstSuggestion, this.renderedElementInFocusIndex, lastSuggestion].find(i =>
      this.isElementInFocus(this.elementList.suggestions[i])
    );
  };

  handleFocusChange = () => {
    const newFocusedSuggestion = this.findFocusedSuggestionIndex();
    if (newFocusedSuggestion >= 0) {
      this.scrollDirectionIsDown = newFocusedSuggestion > this.focusedSuggestionIndex;
      this.focusedSuggestionIndex = newFocusedSuggestion;
    }
  };

  render() {
    this.elementList = this.buildGroups(this.props);
    return (
      <div
        style={{ ...this.state.containerStyle, padding: 0, maxHeight: '320px', overflow: 'auto' }}
        {...this.props}
      >
        <List
          width={300}
          height={300}
          itemCount={this.elementList.suggestions.length}
          itemSize={this.getRowHeight}
          style={{
            maxHeight: this.calcTotalHeight(
              this.elementList.headerOriginalIndices.length,
              this.props.children.length
            )
          }}
          outerRef={this.outerRef}
          ref={this.listRef}
          innerElementType={this.innerElementType}
          onItemsRendered={this.onRowsRendered}
          overscanRowCount={10}
          useIsScrolling
        >
          {this.rowRenderer}
        </List>
      </div>
    );
  }
}

/**
 * This function takes the suggestions transferred to the MentionInput component and flattens them for the draft-js-mention-plugin.
 * @param suggestions - An array of suggestions as transferred to MentionInput.
 * @param getName - A callback that returns the name to display as a suggestion.
 * @param getId - A callback that returns the id of the suggestion.
 * @returns {*}
 */
StickyHeaderListMentionAdapter.buildSuggestions = (suggestions, getName, getId) => {
  return suggestions.reduce((suggestions, group) => {
    const [groupName, items] = group;
    return suggestions.concat(
      items.map(item => {
        const itemExtensions = { type: groupName, name: getName(item), id: getId(item) };
        return { ...item, ...itemExtensions };
      })
    );
  }, []);
};

StickyHeaderListMentionAdapter.theme = {
  mentionSuggestions: 'StickyHeaderListMentionAdapter__suggestions',
  mentionSuggestionsEntry: 'StickyHeaderListMentionAdapter__suggestion-entry',
  mentionSuggestionsEntryFocused: 'StickyHeaderListMentionAdapter__suggestion-entry--focused',
  mentionSuggestionsEntryAvatar: 'StickyHeaderListMentionAdapter__suggestion-entry-avatar',
  mentionSuggestionsEntryText: 'StickyHeaderListMentionAdapter__suggestion-entry-text'
};

StickyHeaderListMentionAdapter.propTypes = {
  /**
   * The list of components generated by MentionSuggestion plugin for draft-js.
   * Each components gets an immutable mention object as a prop.
   */
  children: PropTypes.oneOfType([
    PropTypes.shape({
      props: PropTypes.shape({
        /**
         * The mention object within each child.
         */
        mention: PropTypes.object,
        /**
         * Whether or not this child is focused right now.
         */
        isFocused: PropTypes.bool
      })
    }),
    PropTypes.arrayOf(
      PropTypes.shape({
        props: PropTypes.shape({
          /**
           * The mention object within each child.
           */
          mention: PropTypes.object,
          /**
           * Whether or not this child is focused right now.
           */
          isFocused: PropTypes.bool
        })
      })
    )
  ])
};

export default StickyHeaderListMentionAdapter;
